import type { GenericObjectType } from '@rjsf/utils';
import { isArrayIndex } from './array-path';
import type { SharingConfig } from './types';

/**
 * Navigate to the next schema level for a given path part
 *
 */
export function navigateToNextSchema(
  currentSchema: GenericObjectType,
  fieldPathArray: PropertyKey[],
  fieldPathArrayIx: number,
): GenericObjectType | undefined {
  const part = fieldPathArray[fieldPathArrayIx];
  const isLastPart = fieldPathArrayIx === fieldPathArray.length - 1;

  if (typeof part === 'symbol') return;
  // Direct property access
  if (currentSchema[part]) return currentSchema[part] as GenericObjectType;

  const properties = currentSchema.properties as GenericObjectType;
  // Try properties object (JSON Schema structure)
  if (properties?.[part]) return properties[part] as GenericObjectType;

  // If the last field in the path is an array index, sharing config should be set in the field's schema.
  // Otherwise, if the field is not the last field in the path but is an array index, return the items property if it exists
  // in order to continue navigating the schema to extract the sharing config.
  if (isArrayIndex(part) && isLastPart) return currentSchema as GenericObjectType;
  if (isArrayIndex(part) && currentSchema.items) return currentSchema.items as GenericObjectType;
}

/**
 * Navigate through uiSchema following the field path
 */
export const navigateSchemaPath = (uiSchema: GenericObjectType, fieldPathArray: PropertyKey[]): GenericObjectType | undefined => {
  let ix = 0;
  let currentSchema: GenericObjectType | undefined = uiSchema;
  while (currentSchema && ix < fieldPathArray.length) {
    currentSchema = navigateToNextSchema(currentSchema, fieldPathArray, ix);
    ix++;
  }
  return currentSchema;
};

/**
 * Extract sharing configuration from a schema node
 */
export function extractSharingConfig(schema?: GenericObjectType): SharingConfig | undefined {
  const config = schema?.['ui:variableSharing'];
  if (config) return config as SharingConfig;
}

/**
 * Get sharing configuration for a field from uiSchema
 * @param fieldPathArray - The field path as an array of segments (e.g., ['arguments', 'messaging', 'title'])
 * @param uiSchema - The UI schema object
 * @returns Sharing configuration or undefined if not found
 */
export function getSharingConfig(fieldPathArray: PropertyKey[], uiSchema?: GenericObjectType): SharingConfig | undefined {
  if (!uiSchema || typeof uiSchema !== 'object') return;
  return extractSharingConfig(navigateSchemaPath(uiSchema, fieldPathArray));
}
